<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import AppButton from '@uiKit/elements/Button.vue'
import SuccessTickAnimation from '@uiKit/components/SuccessTickAnimation.vue'

interface Props {
  visible: boolean
  amount: string
  bankName: string
  maskedAccount: string
  submittedAt: string
}

defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:visible', val: boolean): void
  (e: 'done'): void
}>()

const onDone = () => {
  emit('update:visible', false)
  emit('done')
}
</script>

<template>
  <Teleport to="body">
    <Transition name="wsm-fade">
      <Box v-if="visible" class="wsm-backdrop" @click.self="onDone" />
    </Transition>
    <Transition name="wsm-slide">
      <Box
        v-if="visible"
        align="center"
        justify="center"
        class="wsm-wrap"
        role="dialog"
        aria-modal="true"
      >
        <Box direction="col" class="wsm-panel">
          <Box direction="col" align="center" gap="4" class="wsm-body">
            <SuccessTickAnimation />

            <Box direction="col" gap="1" class="wsm-text">
              <Text type="title-lg" align="center" class="wsm-title">Withdrawal Submitted</Text>
              <Text type="body" color="muted" align="center" class="wsm-subtitle">
                Your withdrawal request has been submitted successfully.
              </Text>
            </Box>

            <Box direction="col" gap="2" class="wsm-summary">
              <Box align="center" justify="between" gap="3" class="wsm-row">
                <Text type="body-sm" color="muted" is="span">Amount</Text>
                <Text type="body-sm" weight="semibold" align="right" is="span">
                  AED {{ amount }}
                </Text>
              </Box>
              <Box class="wsm-divider" />
              <Box align="center" justify="between" gap="3" class="wsm-row">
                <Text type="body-sm" color="muted" is="span">To Bank</Text>
                <Text type="body-sm" weight="semibold" align="right" is="span">{{ bankName }}</Text>
              </Box>
              <Box align="center" justify="between" gap="3" class="wsm-row wsm-row--sub">
                <Box class="wsm-label" />
                <Text type="caption" color="muted" align="right" is="span">
                  {{ maskedAccount }}
                </Text>
              </Box>
              <Box class="wsm-divider" />
              <Box align="center" justify="between" gap="3" class="wsm-row">
                <Text type="body-sm" color="muted" is="span">Submitted At</Text>
                <Text type="body-sm" weight="semibold" align="right" is="span">
                  {{ submittedAt }}
                </Text>
              </Box>
            </Box>

            <Text type="caption" color="muted" align="center" class="wsm-note">
              Funds will be transferred to your bank account within 48 hours.
            </Text>
          </Box>

          <Box class="wsm-footer">
            <AppButton label="Done" block class="wsm-btn" @click="onDone" />
          </Box>
        </Box>
      </Box>
    </Transition>
  </Teleport>
</template>

<style scoped>
.wsm-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(16, 24, 40, 0.5);
  z-index: 1000;
}

.wsm-wrap {
  position: fixed;
  inset: 0;
  z-index: 1001;
  padding: 1rem;
  pointer-events: none;
}

.wsm-panel {
  background: var(--p-surface-0);
  border-radius: 16px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
  pointer-events: all;
  overflow: hidden;
}

.wsm-body {
  padding: 2rem 1.75rem 1.5rem;
  text-align: center;
}

.wsm-title {
  margin: 0;
  line-height: 1.3;
}

.wsm-subtitle {
  margin: 0;
  line-height: 1.5;
}

.wsm-summary {
  width: 100%;
  background: var(--p-surface-50);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-lg);
  padding: 1rem 1.25rem;
  text-align: left;
}

.wsm-row--sub {
  margin-top: -6px;
}

.wsm-label {
  flex-shrink: 0;
  min-width: 90px;
}

.wsm-divider {
  height: 1px;
  background: var(--p-content-border-color);
}

.wsm-note {
  margin: 0;
  line-height: 1.5;
}

.wsm-footer {
  padding: 0 1.75rem 1.75rem;
}

.wsm-btn {
  flex: 1;
}

.wsm-fade-enter-active,
.wsm-fade-leave-active {
  transition: opacity 0.22s ease;
}

.wsm-fade-enter-from,
.wsm-fade-leave-to {
  opacity: 0;
}

.wsm-slide-enter-active,
.wsm-slide-leave-active {
  transition:
    opacity 0.22s ease,
    transform 0.22s ease;
}

.wsm-slide-enter-from,
.wsm-slide-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.97);
}
</style>
